import { useContext } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import { useCustomErrors } from "go-form/hooks";
import {
	CsvImportOverviewFormType,
	ExportCSVTranslationsApi,
	ImportCsvOverviewApi,
	ImportOverviewConfig,
} from "../../../../services/types";
import { CsvContext } from "../../services/context";
import { AttributeProps, CsvImportProblemType } from "../../utils/types";
import { getImportOverviewFormDefaultValues } from "../../utils/utils";
import CsvImportProblemCard from "./CsvImportProblemCard";
import CsvValuesMappingTable from "./CsvValuesMappingTable";
import ImportOverviewForm from "./Form/ImportOverviewForm";

interface Props {
	loading: boolean;
	setLoading: React.Dispatch<React.SetStateAction<boolean>>;
	setMappingsWithConfig: React.Dispatch<React.SetStateAction<CsvImportOverviewFormType | undefined>>;
	translations: ExportCSVTranslationsApi;
	importOverviewConfig: ImportOverviewConfig;
	resourceType: string;
	overviewData: ImportCsvOverviewApi;
	selectedFile?: File;
	attributes: AttributeProps[];
	handleImportTimeout: (
		jobUid: string,
		formData: FormData,
		_headers: Record<string, string>,
		params: Record<string, any>,
		customStepNumberOnSuccess?: number
	) => void;
}

// Uncomment on warning table display
// const csvImportProblemsArray = Object.values(CsvImportProblemType);
const csvImportProblemsArray: CsvImportProblemType[] = [CsvImportProblemType.ERRORS];

const ImportOverview = ({
	translations,
	importOverviewConfig,
	resourceType,
	overviewData,
	selectedFile,
	attributes,
	handleImportTimeout,
	loading,
	setLoading,
	setMappingsWithConfig,
}: Props) => {
	const { formSelects } = importOverviewConfig;
	const { t } = useTranslation("lib");
	const form = useForm<CsvImportOverviewFormType>({
		defaultValues: getImportOverviewFormDefaultValues(overviewData, formSelects),
	});
	const { handleSubmit, formState, setError } = form;
	const { addFlash } = useFlash();
	const csvImportContext = useContext(CsvContext);
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);

	const onSubmit = handleSubmit(async (data) => {
		if (!validateCustomErrors()) return;

		try {
			setLoading(true);
			const formData = new FormData();
			if (selectedFile) formData.append("csv", selectedFile);

			formData.append(
				"attributes",
				new Blob(
					[
						JSON.stringify(
							attributes.filter((f: { domain_field_name: string }) => f.domain_field_name?.length > 0)
						),
					],
					{ type: "application/json" }
				)
			);
			formData.append(
				"config",
				new Blob([JSON.stringify(data)], {
					type: "application/json",
				})
			);
			const headers = { "Content-Type": "multipart/form-data" };
			const CancelToken = axios.CancelToken;
			const newCancelToken = CancelToken.source();
			setMappingsWithConfig(data);
			const jobId = await csvImportContext.importAsyncCsv?.(
				resourceType,
				formData,
				headers,
				{},
				{ cancelToken: newCancelToken.token }
			);
			handleImportTimeout(
				jobId,
				formData,
				headers,
				{ include: importOverviewConfig.dryImportProgressIncludes },
				4
			);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	});

	const csvImportProblems = csvImportProblemsArray.map((problem) => {
		const problemObject = overviewData[problem];

		if (!problemObject) return null;

		return (
			<CsvImportProblemCard
				key={problem}
				translations={translations}
				problemType={problem}
				problems={problemObject}
			/>
		);
	});

	return (
		<FormDirty onSubmit={onSubmit} formState={formState}>
			{formSelects && <ImportOverviewForm form={form} formSelects={formSelects} setErrors={setErrors} />}
			<span className="d-block my-4 font-weight-bold">{`${t(
				"go_component.import_csv.field.errors_occurred.title",
				{ resource_name: translations.resource_name }
			)}:`}</span>
			{csvImportProblems}
			<CsvValuesMappingTable
				form={form}
				translations={translations}
				importOverviewConfig={importOverviewConfig}
				setErrors={setErrors}
				attributes={attributes}
				overviewData={overviewData}
			/>
			<div className="form-footer">
				<ButtonLoading loading={loading} type="submit">
					{t("go_component.field.next.title", { ns: "lib" })}
				</ButtonLoading>
			</div>
		</FormDirty>
	);
};

export default ImportOverview;
